import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import api from '../../services/api';
import { useConfirmation } from '../../context/ConfirmationContext';

export default function Grades() {
  const navigate = useNavigate();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [formData, setFormData] = useState({ grade_name: '', description: '' });
  const [error, setError] = useState('');

  useEffect(() => { loadGrades(); }, []);

  const loadGrades = async () => {
    try {
      const res = await api.get('/admin/grades');
      if (res.data.success) setGrades(res.data.grades || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingGrade) {
        await api.put(`/admin/grades/${editingGrade.grade_id}`, formData);
      } else {
        await api.post('/admin/grades', formData);
      }
      setShowModal(false);
      setEditingGrade(null);
      setFormData({ grade_name: '', description: '' });
      loadGrades();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save grade');
    }
  };

  const { showConfirmation } = useConfirmation();

  const openEditModal = (grade) => {
    setEditingGrade(grade);
    setFormData({ grade_name: grade.grade_name, description: grade.description || '' });
    setShowModal(true);
  };

  const confirmDeleteGrade = (grade) => {
    showConfirmation({
      title: 'Delete Grade?',
      message: `Are you sure you want to delete "${grade.grade_name}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/grades/${grade.grade_id}`);
          if (res.data.success) {
            loadGrades();
          }
        } catch (err) {
          setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete grade');
        }
      }
    });
  };

  const openAddModal = () => {
    setEditingGrade(null);
    setFormData({ grade_name: '', description: '' });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Grades</h1>
          <p className="text-gray-500">Configure academic grades/years</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Add Grade
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {grades.map((grade) => (
          <div 
            key={grade.grade_id} 
            onClick={() => navigate(`/admin/grades/${grade.grade_id}`)}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-indigo-200 transition-all cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-indigo-600 transition">
                  {grade.grade_name}
                </h3>
                <p className="text-gray-500 text-sm mt-1">{grade.description || 'No description'}</p>
              </div>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={() => openEditModal(grade)} 
                  className="p-2 hover:bg-indigo-50 text-indigo-600 rounded-lg transition"
                  title="Edit Grade"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => confirmDeleteGrade(grade)} 
                  className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition"
                  title="Delete Grade"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {grades.length === 0 && (
          <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <p className="text-gray-400">No grades configured yet. Add your first grade to get started.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editingGrade ? 'Edit Grade' : 'Add New Grade'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Name *</label>
                <input type="text" required value={formData.grade_name} onChange={(e) => setFormData({...formData, grade_name: e.target.value})}
                  placeholder="e.g., Year 1, Grade 10" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingGrade ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
